import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  AreaChart, Area, Cell, PieChart, Pie
} from 'recharts';
import {
  Eye, Users, CheckCircle, TrendingUp, ArrowUpRight, ArrowDownRight,
  BarChart3, PieChart as PieChartIcon, Activity, Clock, Share2, Link2,
  Monitor, Calculator, Bell, FileText, Building2, AlertTriangle, Receipt,
  DollarSign, ShoppingCart, Factory, RefreshCw, Flame, Grid3X3,
  GitMerge, UserPlus, LogIn, ArrowDown, FlaskConical, Trophy, Percent,
  Plus, X, GripVertical, ToggleLeft, ToggleRight, Zap,
  AlertCircle, Settings, BellRing, ShieldAlert, TrendingDown, Save
} from 'lucide-react';
import api from '@/lib/api';

const heading = { fontFamily: "'Outfit', sans-serif" };

const fadeInStyle = document.createElement('style');
fadeInStyle.textContent = '@keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }';
if (!document.querySelector('[data-fade-in-style]')) {
  fadeInStyle.setAttribute('data-fade-in-style', '');
  document.head.appendChild(fadeInStyle);
}

const STEP_ICONS = {
  dashboard: BarChart3, pos: Monitor, hr: Users, accounting: Calculator,
  analytics: TrendingUp, notifications: Bell, documents: FileText,
  branches: Building2, alerts: AlertTriangle, expenses: Receipt, pnl: DollarSign,
};
const STEP_LABELS = {
  dashboard: 'Dashboard', pos: 'POS', hr: 'HR & Payroll', accounting: 'Accounting',
  analytics: 'Analytics', notifications: 'Notifications', documents: 'Documents',
  branches: 'Branches', alerts: 'Alerts', expenses: 'Expenses', pnl: 'Profit & Loss',
};
const STEP_COLORS = [
  '#f97316', '#3b82f6', '#10b981', '#8b5cf6', '#06b6d4',
  '#ef4444', '#6366f1', '#f59e0b', '#ec4899', '#f43f5e', '#059669',
];

function KpiCard({ label, value, sub, icon: Icon, color, delay = 0 }) {
  return (
    <motion.div className="bg-white rounded-xl border border-gray-100 p-5 shadow-sm"
      initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
      transition={{ delay, duration: 0.4 }}
      data-testid={`kpi-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="flex items-center justify-between mb-3">
        <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${color} flex items-center justify-center`}>
          <Icon className="w-5 h-5 text-white" />
        </div>
        {sub && (
          <span className={`text-[11px] font-bold flex items-center gap-0.5 ${
            sub.startsWith('+') ? 'text-success' : sub.startsWith('-') ? 'text-error' : 'text-[#86868B]'
          }`}>
            {sub.startsWith('+') && <ArrowUpRight className="w-3 h-3" />}
            {sub.startsWith('-') && <ArrowDownRight className="w-3 h-3" />}
            {sub}
          </span>
        )}
      </div>
      <p className="text-3xl font-bold text-[#1D1D1F] tracking-tight" style={heading}>{value}</p>
      <p className="text-[13px] text-[#86868B] mt-1">{label}</p>
    </motion.div>
  );
}

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white/95 backdrop-blur-sm border border-gray-100 rounded-lg px-3 py-2 shadow-lg">
      <p className="text-[11px] font-semibold text-[#1D1D1F]">{label}</p>
      {payload.map((p, i) => (
        <p key={i} className="text-[11px]" style={{ color: p.color }}>
          {p.name}: <span className="font-semibold">{p.value}</span>
        </p>
      ))}
    </div>
  );
}

export default function DemoAnalyticsPage() {
  const [stats, setStats] = useState(null);
  const [heatmap, setHeatmap] = useState(null);
  const [funnel, setFunnel] = useState(null);
  const [abResults, setAbResults] = useState(null);
  const [alertsData, setAlertsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('overview');

  const fetchStats = async () => {
    setLoading(true);
    try {
      const [statsRes, heatmapRes, funnelRes, abRes, alertsRes] = await Promise.all([
        api.get('/demo-analytics/stats'),
        api.get('/demo-analytics/heatmap'),
        api.get('/demo-analytics/funnel'),
        api.get('/demo-analytics/ab/results'),
        api.get('/demo-analytics/alerts'),
      ]);
      setStats(statsRes.data);
      setHeatmap(heatmapRes.data);
      setFunnel(funnelRes.data);
      setAbResults(abRes.data);
      setAlertsData(alertsRes.data);
    } catch {
      setStats(null);
      setHeatmap(null);
      setFunnel(null);
      setAbResults(null);
      setAlertsData(null);
    }
    setLoading(false);
  };

  useEffect(() => { fetchStats(); }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]" data-testid="demo-analytics-loading">
        <div className="text-center">
          <div className="w-10 h-10 border-3 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
          <p className="text-[13px] text-[#86868B]">Loading analytics...</p>
        </div>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]" data-testid="demo-analytics-error">
        <div className="text-center">
          <p className="text-[15px] font-medium text-[#1D1D1F]">Unable to load analytics</p>
          <button onClick={fetchStats} className="text-[13px] text-orange-500 mt-2">Try again</button>
        </div>
      </div>
    );
  }

  const stepChartData = (stats.step_views || []).map((s, i) => ({
    name: STEP_LABELS[s.step_id] || s.step_id,
    views: s.views,
    fill: STEP_COLORS[i % STEP_COLORS.length],
  }));

  const dropoffData = (stats.dropoff || []).map(d => ({
    name: STEP_LABELS[Object.keys(STEP_LABELS)[d.step_index]] || `Step ${d.step_index + 1}`,
    sessions: d.sessions_dropped,
  }));

  const sourceData = Object.entries(stats.sources || {}).map(([key, val]) => ({
    name: key === 'shared_link' ? 'Shared Link' : 'Direct',
    value: val,
    fill: key === 'shared_link' ? '#3b82f6' : '#f97316',
  }));

  const dailyData = (stats.daily_sessions || []).map(d => ({
    date: d.date.slice(5),
    sessions: d.count,
  }));

  const totalDirect = stats.sources?.direct || 0;
  const totalShared = stats.sources?.shared_link || 0;

  return (
    <div className="space-y-6 pb-8" data-testid="demo-analytics-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#1D1D1F] tracking-tight" style={heading}>Demo Analytics</h1>
          <p className="text-[13px] text-[#86868B] mt-1">Track how visitors interact with your product tour</p>
        </div>
        <button onClick={fetchStats}
          className="flex items-center gap-2 px-4 py-2 bg-[#1D1D1F] hover:bg-black text-white rounded-xl text-[13px] font-medium transition-colors"
          data-testid="refresh-analytics-btn">
          <RefreshCw className="w-4 h-4" /> Refresh
        </button>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 bg-gray-100 rounded-xl p-1 w-fit" data-testid="analytics-tabs">
        <button onClick={() => setTab('overview')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-[13px] font-medium transition-all ${
            tab === 'overview' ? 'bg-white text-[#1D1D1F] shadow-sm' : 'text-[#86868B] hover:text-[#1D1D1F]'
          }`}
          data-testid="tab-overview">
          <BarChart3 className="w-4 h-4" /> Overview
        </button>
        <button onClick={() => setTab('heatmap')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-[13px] font-medium transition-all ${
            tab === 'heatmap' ? 'bg-white text-[#1D1D1F] shadow-sm' : 'text-[#86868B] hover:text-[#1D1D1F]'
          }`}
          data-testid="tab-heatmap">
          <Flame className="w-4 h-4" /> Heatmap
        </button>
        <button onClick={() => setTab('funnel')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-[13px] font-medium transition-all ${
            tab === 'funnel' ? 'bg-white text-[#1D1D1F] shadow-sm' : 'text-[#86868B] hover:text-[#1D1D1F]'
          }`}
          data-testid="tab-funnel">
          <GitMerge className="w-4 h-4" /> Funnel
        </button>
        <button onClick={() => setTab('ab')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-[13px] font-medium transition-all ${
            tab === 'ab' ? 'bg-white text-[#1D1D1F] shadow-sm' : 'text-[#86868B] hover:text-[#1D1D1F]'
          }`}
          data-testid="tab-ab">
          <FlaskConical className="w-4 h-4" /> A/B Testing
        </button>
        <button onClick={() => setTab('alerts')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-[13px] font-medium transition-all ${
            tab === 'alerts' ? 'bg-white text-[#1D1D1F] shadow-sm' : 'text-[#86868B] hover:text-[#1D1D1F]'
          }`}
          data-testid="tab-alerts">
          <BellRing className="w-4 h-4" /> Alerts
          {alertsData?.alerts?.length > 0 && (
            <span className="w-5 h-5 rounded-full bg-error text-white text-[9px] font-bold flex items-center justify-center">
              {alertsData.alerts.length}
            </span>
          )}
        </button>
      </div>

      {tab === 'overview' && (<>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard label="Total Tour Views" value={stats.total_sessions} icon={Eye} color="from-orange-500 to-amber-500" delay={0.05} />
        <KpiCard label="Last 7 Days" value={stats.sessions_7d} sub={stats.sessions_7d > 0 ? 'active' : '0'} icon={Activity} color="from-blue-500 to-indigo-500" delay={0.1} />
        <KpiCard label="Completed Tours" value={stats.completed} icon={CheckCircle} color="from-emerald-500 to-teal-500" delay={0.15} />
        <KpiCard label="Completion Rate" value={`${stats.completion_rate}%`} icon={TrendingUp} color="from-violet-500 to-purple-500" delay={0.2} />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Step Views Bar Chart */}
        <motion.div className="lg:col-span-2 bg-white rounded-xl border border-gray-100 p-5 shadow-sm"
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }}
          data-testid="step-views-chart">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-[15px] font-semibold text-[#1D1D1F]" style={heading}>Step Views</h2>
              <p className="text-[11px] text-[#86868B]">How many times each step was viewed</p>
            </div>
            <BarChart3 className="w-5 h-5 text-[#86868B]" />
          </div>
          {stepChartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={stepChartData} margin={{ top: 0, right: 0, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="name" tick={{ fontSize: 10, fill: '#86868B' }} angle={-30} textAnchor="end" height={60} />
                <YAxis tick={{ fontSize: 10, fill: '#86868B' }} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="views" radius={[6, 6, 0, 0]} name="Views">
                  {stepChartData.map((entry, i) => (
                    <Cell key={i} fill={entry.fill} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-64 flex items-center justify-center text-[13px] text-[#86868B]">No step view data yet</div>
          )}
        </motion.div>

        {/* Source Breakdown Pie */}
        <motion.div className="bg-white rounded-xl border border-gray-100 p-5 shadow-sm"
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
          data-testid="source-chart">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-[15px] font-semibold text-[#1D1D1F]" style={heading}>Traffic Source</h2>
              <p className="text-[11px] text-[#86868B]">Direct vs Shared links</p>
            </div>
            <PieChartIcon className="w-5 h-5 text-[#86868B]" />
          </div>
          {sourceData.length > 0 ? (
            <div className="flex flex-col items-center">
              <ResponsiveContainer width="100%" height={170}>
                <PieChart>
                  <Pie data={sourceData} cx="50%" cy="50%" innerRadius={45} outerRadius={70} dataKey="value" stroke="none">
                    {sourceData.map((entry, i) => (
                      <Cell key={i} fill={entry.fill} />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                </PieChart>
              </ResponsiveContainer>
              <div className="flex items-center gap-4 mt-2">
                <div className="flex items-center gap-1.5">
                  <div className="w-2.5 h-2.5 rounded-full bg-orange-500" />
                  <span className="text-[11px] text-[#86868B]">Direct ({totalDirect})</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-2.5 h-2.5 rounded-full bg-info" />
                  <span className="text-[11px] text-[#86868B]">Shared ({totalShared})</span>
                </div>
              </div>
            </div>
          ) : (
            <div className="h-48 flex items-center justify-center text-[13px] text-[#86868B]">No source data yet</div>
          )}
        </motion.div>
      </div>

      {/* Daily Sessions & Drop-off */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Daily Sessions */}
        <motion.div className="bg-white rounded-xl border border-gray-100 p-5 shadow-sm"
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35 }}
          data-testid="daily-sessions-chart">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-[15px] font-semibold text-[#1D1D1F]" style={heading}>Daily Sessions</h2>
              <p className="text-[11px] text-[#86868B]">Tour opens per day (last 30 days)</p>
            </div>
            <Activity className="w-5 h-5 text-[#86868B]" />
          </div>
          {dailyData.length > 0 ? (
            <ResponsiveContainer width="100%" height={200}>
              <AreaChart data={dailyData} margin={{ top: 0, right: 0, left: -20, bottom: 0 }}>
                <defs>
                  <linearGradient id="dailyGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#f97316" stopOpacity={0.2} />
                    <stop offset="95%" stopColor="#f97316" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="date" tick={{ fontSize: 9, fill: '#86868B' }} />
                <YAxis tick={{ fontSize: 10, fill: '#86868B' }} />
                <Tooltip content={<CustomTooltip />} />
                <Area type="monotone" dataKey="sessions" stroke="#f97316" strokeWidth={2} fill="url(#dailyGrad)" name="Sessions" />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-48 flex items-center justify-center text-[13px] text-[#86868B]">No daily data yet</div>
          )}
        </motion.div>

        {/* Drop-off Funnel */}
        <motion.div className="bg-white rounded-xl border border-gray-100 p-5 shadow-sm"
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}
          data-testid="dropoff-chart">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-[15px] font-semibold text-[#1D1D1F]" style={heading}>Drop-off Points</h2>
              <p className="text-[11px] text-[#86868B]">Where visitors stopped the tour</p>
            </div>
            <TrendingUp className="w-5 h-5 text-[#86868B]" />
          </div>
          {dropoffData.length > 0 ? (
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={dropoffData} layout="vertical" margin={{ top: 0, right: 10, left: 10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis type="number" tick={{ fontSize: 10, fill: '#86868B' }} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 9, fill: '#86868B' }} width={85} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="sessions" fill="#ef4444" radius={[0, 6, 6, 0]} name="Sessions dropped" />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-48 flex items-center justify-center text-[13px] text-[#86868B]">No drop-off data yet</div>
          )}
        </motion.div>
      </div>

      {/* Recent Sessions Table */}
      <motion.div className="bg-white rounded-xl border border-gray-100 p-5 shadow-sm"
        initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.45 }}
        data-testid="recent-sessions-table">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-[15px] font-semibold text-[#1D1D1F]" style={heading}>Recent Sessions</h2>
            <p className="text-[11px] text-[#86868B]">Last 10 visitors who tried the demo</p>
          </div>
          <Clock className="w-5 h-5 text-[#86868B]" />
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="text-[10px] font-bold text-[#86868B] uppercase tracking-wider border-b border-gray-100">
                <th className="py-2 px-3 text-left">Session</th>
                <th className="py-2 px-3 text-left">Source</th>
                <th className="py-2 px-3 text-left">Started</th>
                <th className="py-2 px-3 text-left">Last Step</th>
                <th className="py-2 px-3 text-left">Progress</th>
                <th className="py-2 px-3 text-left">Status</th>
              </tr>
            </thead>
            <tbody>
              {(stats.recent_sessions || []).length > 0 ? (
                stats.recent_sessions.map((s, i) => {
                  const StepIcon = STEP_ICONS[s.last_step] || BarChart3;
                  const progress = Math.round(((s.last_step_index + 1) / 11) * 100);
                  return (
                    <tr key={i} className="border-b border-gray-50 hover:bg-gray-50/50 text-[12px]"
                      style={{ animation: `fadeIn 0.3s ${0.5 + i * 0.04}s both` }}>
                      <td className="py-2.5 px-3 font-mono text-[11px] text-[#86868B]">{s.session_id}</td>
                      <td className="py-2.5 px-3">
                        <span className={`inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full ${
                          s.source === 'shared_link'
                            ? 'bg-info/10 text-info'
                            : 'bg-orange-50 text-orange-600'
                        }`}>
                          {s.source === 'shared_link' ? <Link2 className="w-2.5 h-2.5" /> : <Share2 className="w-2.5 h-2.5" />}
                          {s.source === 'shared_link' ? 'Shared' : 'Direct'}
                        </span>
                      </td>
                      <td className="py-2.5 px-3 text-[11px] text-[#86868B]">
                        {new Date(s.started_at).toLocaleDateString()} {new Date(s.started_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </td>
                      <td className="py-2.5 px-3">
                        <div className="flex items-center gap-1.5">
                          <StepIcon className="w-3.5 h-3.5 text-[#86868B]" />
                          <span className="text-[11px] text-[#1D1D1F] font-medium">{STEP_LABELS[s.last_step] || s.last_step}</span>
                        </div>
                      </td>
                      <td className="py-2.5 px-3">
                        <div className="flex items-center gap-2">
                          <div className="w-16 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                            <div className={`h-full rounded-full ${s.completed ? 'bg-success' : 'bg-orange-400'}`}
                              style={{ width: `${progress}%` }} />
                          </div>
                          <span className="text-[9px] text-[#86868B] font-medium">{progress}%</span>
                        </div>
                      </td>
                      <td className="py-2.5 px-3">
                        <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${
                          s.completed ? 'bg-success/10 text-success' : 'bg-warning/10 text-warning'
                        }`}>
                          {s.completed ? 'Completed' : 'Dropped'}
                        </span>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={6} className="py-8 text-center text-[13px] text-[#86868B]">No sessions yet. Share the demo link to start tracking!</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </motion.div>

      </>)}

      {tab === 'heatmap' && <HeatmapView heatmap={heatmap} stats={stats} />}

      {tab === 'funnel' && <FunnelView funnel={funnel} />}

      {tab === 'ab' && <ABTestingView abResults={abResults} onRefresh={fetchStats} />}

      {tab === 'alerts' && <AlertsView alertsData={alertsData} onRefresh={fetchStats} />}

    </div>
  );
}


/* ═══════════════════════════════════════════
   HEATMAP VIEW
   ═══════════════════════════════════════════ */

const STEP_ORDER = ['dashboard', 'pos', 'hr', 'accounting', 'analytics', 'notifications', 'documents', 'branches', 'alerts', 'expenses', 'pnl'];
const STEP_FULL_COLORS = {
  dashboard: { bg: 'from-orange-500 to-amber-500', light: 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-200' },
  pos: { bg: 'from-blue-500 to-indigo-600', light: 'bg-info/10', text: 'text-info', border: 'border-info/30' },
  hr: { bg: 'from-emerald-500 to-teal-600', light: 'bg-success/10', text: 'text-success', border: 'border-success/30' },
  accounting: { bg: 'from-violet-500 to-purple-600', light: 'bg-violet-50', text: 'text-violet-700', border: 'border-violet-200' },
  analytics: { bg: 'from-cyan-500 to-blue-600', light: 'bg-cyan-50', text: 'text-cyan-700', border: 'border-cyan-200' },
  notifications: { bg: 'from-red-500 to-rose-600', light: 'bg-error/10', text: 'text-error', border: 'border-error/30' },
  documents: { bg: 'from-indigo-500 to-blue-600', light: 'bg-indigo-50', text: 'text-indigo-700', border: 'border-indigo-200' },
  branches: { bg: 'from-amber-500 to-orange-600', light: 'bg-warning/10', text: 'text-warning', border: 'border-warning/30' },
  alerts: { bg: 'from-rose-500 to-red-600', light: 'bg-error/10', text: 'text-error', border: 'border-error/30' },
  expenses: { bg: 'from-pink-500 to-rose-600', light: 'bg-pink-50', text: 'text-pink-700', border: 'border-pink-200' },
  pnl: { bg: 'from-emerald-600 to-green-600', light: 'bg-success/10', text: 'text-success', border: 'border-success/30' },
};

function getHeatColor(value, max) {
  if (max === 0) return { bg: 'bg-gray-50', text: 'text-gray-400' };
  const pct = value / max;
  if (pct >= 0.8) return { bg: 'bg-error', text: 'text-white' };
  if (pct >= 0.6) return { bg: 'bg-orange-400', text: 'text-white' };
  if (pct >= 0.4) return { bg: 'bg-amber-300', text: 'text-warning' };
  if (pct >= 0.2) return { bg: 'bg-yellow-200', text: 'text-yellow-900' };
  if (pct > 0) return { bg: 'bg-success/20', text: 'text-green-800' };
  return { bg: 'bg-gray-50', text: 'text-gray-400' };
}

function HeatmapView({ heatmap, stats }) {
  const engagement = heatmap?.engagement || [];
  const maxViews = Math.max(...engagement.map(e => e.views), 1);
  const maxTime = Math.max(...engagement.map(e => e.avg_time), 1);
  const maxScore = Math.max(...engagement.map(e => e.score), 1);

  // Build lookup
  const engMap = {};
  engagement.forEach(e => { engMap[e.step_id] = e; });

  return (
    <div className="space-y-6" data-testid="heatmap-view">
      {/* Step Engagement Matrix */}
      <motion.div className="bg-white rounded-xl border border-gray-100 p-5 shadow-sm"
        initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}
        data-testid="engagement-matrix">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h2 className="text-[15px] font-semibold text-[#1D1D1F]" style={heading}>Step Engagement Matrix</h2>
            <p className="text-[11px] text-[#86868B]">Views, average time, and engagement score per step — darker = higher</p>
          </div>
          <Grid3X3 className="w-5 h-5 text-[#86868B]" />
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="text-[10px] font-bold text-[#86868B] uppercase tracking-wider">
                <th className="py-2 px-3 text-left w-36">Step</th>
                <th className="py-2 px-3 text-center">Views</th>
                <th className="py-2 px-3 text-center">Avg Time (s)</th>
                <th className="py-2 px-3 text-center">Engagement Score</th>
                <th className="py-2 px-3 text-left w-48">Heat</th>
              </tr>
            </thead>
            <tbody>
              {STEP_ORDER.map((sid, i) => {
                const data = engMap[sid] || { views: 0, avg_time: 0, score: 0 };
                const Icon = STEP_ICONS[sid] || BarChart3;
                const colors = STEP_FULL_COLORS[sid];
                const viewColor = getHeatColor(data.views, maxViews);
                const timeColor = getHeatColor(data.avg_time, maxTime);
                const scoreColor = getHeatColor(data.score, maxScore);
                const heatPct = maxScore > 0 ? Math.round((data.score / maxScore) * 100) : 0;
                return (
                  <motion.tr key={sid} className="border-b border-gray-50"
                    initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.1 + i * 0.04 }}>
                    <td className="py-2.5 px-3">
                      <div className="flex items-center gap-2">
                        <div className={`w-7 h-7 rounded-lg bg-gradient-to-br ${colors.bg} flex items-center justify-center`}>
                          <Icon className="w-3.5 h-3.5 text-white" />
                        </div>
                        <span className="text-[12px] font-medium text-[#1D1D1F]">{STEP_LABELS[sid]}</span>
                      </div>
                    </td>
                    <td className="py-2.5 px-3 text-center">
                      <span className={`inline-block px-3 py-1 rounded-lg text-[12px] font-bold ${viewColor.bg} ${viewColor.text}`}>
                        {data.views}
                      </span>
                    </td>
                    <td className="py-2.5 px-3 text-center">
                      <span className={`inline-block px-3 py-1 rounded-lg text-[12px] font-bold ${timeColor.bg} ${timeColor.text}`}>
                        {data.avg_time > 0 ? `${data.avg_time}s` : '—'}
                      </span>
                    </td>
                    <td className="py-2.5 px-3 text-center">
                      <span className={`inline-block px-3 py-1 rounded-lg text-[12px] font-bold ${scoreColor.bg} ${scoreColor.text}`}>
                        {data.score > 0 ? data.score.toFixed(0) : '—'}
                      </span>
                    </td>
                    <td className="py-2.5 px-3">
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-3 bg-gray-100 rounded-full overflow-hidden">
                          <motion.div
                            className="h-full rounded-full"
                            style={{
                              background: heatPct >= 80 ? 'linear-gradient(90deg, #ef4444, #dc2626)' :
                                heatPct >= 60 ? 'linear-gradient(90deg, #f97316, #ea580c)' :
                                heatPct >= 40 ? 'linear-gradient(90deg, #fbbf24, #f59e0b)' :
                                heatPct >= 20 ? 'linear-gradient(90deg, #a3e635, #84cc16)' :
                                'linear-gradient(90deg, #d1d5db, #9ca3af)',
                            }}
                            initial={{ width: 0 }}
                            animate={{ width: `${heatPct}%` }}
                            transition={{ duration: 0.6, delay: 0.2 + i * 0.05 }}
                          />
                        </div>
                        <span className="text-[10px] font-medium text-[#86868B] w-8 text-right">{heatPct}%</span>
                      </div>
                    </td>
                  </motion.tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {/* Legend */}
        <div className="flex items-center justify-center gap-3 mt-4 pt-3 border-t border-gray-50">
          <span className="text-[9px] text-[#86868B] font-medium">Heat Scale:</span>
          {[
            { label: 'Cold', bg: 'bg-success/20' },
            { label: 'Low', bg: 'bg-yellow-200' },
            { label: 'Medium', bg: 'bg-amber-300' },
            { label: 'Hot', bg: 'bg-orange-400' },
            { label: 'Very Hot', bg: 'bg-error' },
          ].map(l => (
            <div key={l.label} className="flex items-center gap-1">
              <div className={`w-3 h-3 rounded-sm ${l.bg}`} />
              <span className="text-[9px] text-[#86868B]">{l.label}</span>
            </div>
          ))}
        </div>
      </motion.div>

      {/* Feature Interest Map */}
      <motion.div className="bg-white rounded-xl border border-gray-100 p-5 shadow-sm"
        initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}
        data-testid="feature-interest-map">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h2 className="text-[15px] font-semibold text-[#1D1D1F]" style={heading}>Feature Interest Map</h2>
            <p className="text-[11px] text-[#86868B]">Visual hotspot map showing which features attract the most attention</p>
          </div>
          <Flame className="w-5 h-5 text-[#86868B]" />
        </div>

        {/* Mock App Layout with Heat Overlays */}
        <div className="relative bg-[#F5F5F7] rounded-xl overflow-hidden border border-gray-200" style={{ minHeight: 420 }}>
          {/* Mock Sidebar */}
          <div className="absolute left-0 top-0 bottom-0 w-40 bg-[#1D1D1F] p-3 z-10">
            <div className="flex items-center gap-2 mb-4 px-1">
              <div className="w-5 h-5 rounded bg-gradient-to-br from-orange-500 to-amber-500" />
              <span className="text-[9px] font-bold text-white tracking-wider">SMARTO</span>
            </div>
            <div className="space-y-0.5">
              {STEP_ORDER.map((sid) => {
                const Icon = STEP_ICONS[sid] || BarChart3;
                const data = engMap[sid] || { score: 0 };
                const intensity = maxScore > 0 ? data.score / maxScore : 0;
                return (
                  <div key={sid} className="relative flex items-center gap-2 px-2 py-1.5 rounded-md" style={{
                    backgroundColor: intensity > 0 ? `rgba(249, 115, 22, ${Math.min(intensity * 0.6, 0.5)})` : 'transparent',
                  }}>
                    <Icon className="w-3 h-3 text-white/60" />
                    <span className="text-[9px] text-white/60 font-medium">{STEP_LABELS[sid]}</span>
                    {intensity >= 0.6 && (
                      <motion.div className="absolute -right-0.5 top-1/2 -translate-y-1/2 w-1.5 h-1.5 rounded-full bg-orange-400"
                        animate={{ scale: [1, 1.5, 1], opacity: [1, 0.5, 1] }}
                        transition={{ duration: 2, repeat: Infinity }} />
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Main Content Area with Feature Hotspots */}
          <div className="ml-40 p-4">
            {/* Top Bar */}
            <div className="h-9 bg-white rounded-lg border border-gray-100 mb-4 flex items-center justify-between px-3">
              <span className="text-[10px] font-semibold text-[#1D1D1F]">Feature Interest</span>
              <div className="flex items-center gap-1.5">
                <div className="w-5 h-5 rounded-full bg-gray-100" />
                <div className="w-5 h-5 rounded-full bg-gray-100" />
                <div className="w-5 h-5 rounded-full bg-gradient-to-br from-orange-400 to-amber-400" />
              </div>
            </div>

            {/* Feature Grid with Heat */}
            <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {STEP_ORDER.map((sid, i) => {
                const Icon = STEP_ICONS[sid] || BarChart3;
                const data = engMap[sid] || { views: 0, avg_time: 0, score: 0 };
                const colors = STEP_FULL_COLORS[sid];
                const intensity = maxScore > 0 ? data.score / maxScore : 0;
                const heatPct = Math.round(intensity * 100);

                return (
                  <motion.div key={sid}
                    className="relative rounded-xl overflow-hidden"
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.2 + i * 0.05 }}>
                    {/* Heat Glow */}
                    {intensity > 0 && (
                      <motion.div className="absolute inset-0 rounded-xl z-0"
                        style={{
                          background: `radial-gradient(circle at center, ${
                            intensity >= 0.8 ? 'rgba(239,68,68,0.25)' :
                            intensity >= 0.6 ? 'rgba(249,115,22,0.2)' :
                            intensity >= 0.4 ? 'rgba(251,191,36,0.15)' :
                            'rgba(163,230,53,0.1)'
                          }, transparent 70%)`,
                        }}
                        animate={intensity >= 0.6 ? { scale: [1, 1.05, 1], opacity: [0.8, 1, 0.8] } : {}}
                        transition={{ duration: 3, repeat: Infinity }}
                      />
                    )}
                    <div className={`relative z-10 ${colors.light} border ${colors.border} rounded-xl p-3 h-full`}>
                      <div className="flex items-center justify-between mb-2">
                        <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${colors.bg} flex items-center justify-center`}>
                          <Icon className="w-4 h-4 text-white" />
                        </div>
                        {intensity >= 0.5 && (
                          <motion.div
                            className="flex items-center gap-0.5 px-1.5 py-0.5 bg-error rounded-full"
                            animate={{ scale: [1, 1.1, 1] }}
                            transition={{ duration: 1.5, repeat: Infinity }}>
                            <Flame className="w-2.5 h-2.5 text-white" />
                            <span className="text-[7px] font-bold text-white">HOT</span>
                          </motion.div>
                        )}
                      </div>
                      <p className={`text-[11px] font-semibold ${colors.text}`}>{STEP_LABELS[sid]}</p>
                      <div className="mt-2 space-y-1">
                        <div className="flex justify-between text-[9px]">
                          <span className="text-[#86868B]">Views</span>
                          <span className={`font-bold ${colors.text}`}>{data.views}</span>
                        </div>
                        <div className="flex justify-between text-[9px]">
                          <span className="text-[#86868B]">Avg Time</span>
                          <span className={`font-bold ${colors.text}`}>{data.avg_time > 0 ? `${data.avg_time}s` : '—'}</span>
                        </div>
                        <div className="h-1.5 bg-white/60 rounded-full overflow-hidden mt-1">
                          <motion.div className="h-full rounded-full"
                            style={{
                              background: heatPct >= 80 ? 'linear-gradient(90deg, #ef4444, #dc2626)' :
                                heatPct >= 60 ? 'linear-gradient(90deg, #f97316, #ea580c)' :
                                heatPct >= 40 ? 'linear-gradient(90deg, #fbbf24, #f59e0b)' :
                                heatPct >= 20 ? 'linear-gradient(90deg, #a3e635, #84cc16)' :
                                'linear-gradient(90deg, #d1d5db, #9ca3af)',
                            }}
                            initial={{ width: 0 }}
                            animate={{ width: `${Math.max(heatPct, 3)}%` }}
                            transition={{ duration: 0.6, delay: 0.3 + i * 0.06 }} />
                        </div>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>

            {/* Bottom Legend */}
            <div className="flex items-center justify-between mt-4 pt-3 border-t border-gray-200">
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <div className="w-20 h-2 rounded-full" style={{ background: 'linear-gradient(90deg, #bbf7d0, #a3e635, #fbbf24, #f97316, #ef4444)' }} />
                  <span className="text-[9px] text-[#86868B]">Low to High Interest</span>
                </div>
                <div className="flex items-center gap-1">
                  <motion.div className="w-2 h-2 rounded-full bg-orange-400"
                    animate={{ scale: [1, 1.5, 1] }} transition={{ duration: 2, repeat: Infinity }} />
                  <span className="text-[9px] text-[#86868B]">= Pulse = High Activity</span>
                </div>
              </div>
              <div className="flex items-center gap-1.5 px-2 py-1 bg-error/10 rounded-md border border-error/30">
                <Flame className="w-3 h-3 text-error" />
                <span className="text-[9px] font-medium text-error">HOT = 50%+ engagement</span>
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Quick Insights */}
      <motion.div className="bg-white rounded-xl border border-gray-100 p-5 shadow-sm"
        initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }}
        data-testid="heatmap-insights">
        <h2 className="text-[15px] font-semibold text-[#1D1D1F] mb-4" style={heading}>Quick Insights</h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {(() => {
            const sorted = [...engagement].sort((a, b) => b.score - a.score);
            const hottest = sorted[0];
            const coldest = sorted.filter(s => s.views > 0).pop() || sorted[sorted.length - 1];
            const avgTime = engagement.length > 0
              ? (engagement.reduce((s, e) => s + (e.avg_time || 0), 0) / engagement.filter(e => e.avg_time > 0).length || 0).toFixed(1)
              : '0';

            return (
              <>
                <div className="bg-error/10 rounded-xl p-4 border border-error/30">
                  <div className="flex items-center gap-2 mb-2">
                    <Flame className="w-4 h-4 text-error" />
                    <span className="text-[11px] font-bold text-error">Hottest Feature</span>
                  </div>
                  <p className="text-[16px] font-bold text-[#1D1D1F]" style={heading}>
                    {hottest ? STEP_LABELS[hottest.step_id] : 'N/A'}
                  </p>
                  <p className="text-[10px] text-[#86868B] mt-0.5">
                    {hottest ? `${hottest.views} views · ${hottest.avg_time}s avg time` : 'No data yet'}
                  </p>
                </div>
                <div className="bg-info/10 rounded-xl p-4 border border-info/30">
                  <div className="flex items-center gap-2 mb-2">
                    <Activity className="w-4 h-4 text-info" />
                    <span className="text-[11px] font-bold text-info">Avg Time Per Step</span>
                  </div>
                  <p className="text-[16px] font-bold text-[#1D1D1F]" style={heading}>{avgTime}s</p>
                  <p className="text-[10px] text-[#86868B] mt-0.5">Across all active steps</p>
                </div>
                <div className="bg-success/10 rounded-xl p-4 border border-success/30">
                  <div className="flex items-center gap-2 mb-2">
                    <Eye className="w-4 h-4 text-success" />
                    <span className="text-[11px] font-bold text-success">Needs Attention</span>
                  </div>
                  <p className="text-[16px] font-bold text-[#1D1D1F]" style={heading}>
                    {coldest ? STEP_LABELS[coldest.step_id] : 'N/A'}
                  </p>
                  <p className="text-[10px] text-[#86868B] mt-0.5">
                    {coldest ? `Lowest engagement — consider improving this section` : 'No data yet'}
                  </p>
                </div>
              </>
            );
          })()}
        </div>
      </motion.div>
    </div>
  );
}


/* ═══════════════════════════════════════════
   FUNNEL VIEW
   ═══════════════════════════════════════════ */

const FUNNEL_ICONS = [Eye, GitMerge, CheckCircle, UserPlus, LogIn];
const FUNNEL_COLORS = [
  { bg: 'from-blue-500 to-indigo-600', light: 'bg-info/10', text: 'text-info', bar: '#3b82f6', border: 'border-info/30' },
  { bg: 'from-orange-500 to-amber-500', light: 'bg-orange-50', text: 'text-orange-700', bar: '#f97316', border: 'border-orange-200' },
  { bg: 'from-emerald-500 to-teal-600', light: 'bg-success/10', text: 'text-success', bar: '#10b981', border: 'border-success/30' },
  { bg: 'from-violet-500 to-purple-600', light: 'bg-violet-50', text: 'text-violet-700', bar: '#8b5cf6', border: 'border-violet-200' },
  { bg: 'from-rose-500 to-red-600', light: 'bg-error/10', text: 'text-error', bar: '#f43f5e', border: 'border-error/30' },
];

function FunnelView({ funnel }) {
  if (!funnel) return <div className="text-center py-20 text-[13px] text-[#86868B]">No funnel data available</div>;

  const stages = funnel.stages || [];
  const maxCount = Math.max(...stages.map(s => s.count), 1);

  return (
    <div className="space-y-6" data-testid="funnel-view">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <motion.div className="bg-white rounded-xl border border-gray-100 p-5 shadow-sm"
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}
          data-testid="funnel-total-visitors">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
              <Eye className="w-5 h-5 text-white" />
            </div>
          </div>
          <p className="text-3xl font-bold text-[#1D1D1F] tracking-tight" style={heading}>{funnel.total_visitors}</p>
          <p className="text-[13px] text-[#86868B] mt-1">Total Visitors</p>
        </motion.div>
        <motion.div className="bg-white rounded-xl border border-gray-100 p-5 shadow-sm"
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
          data-testid="funnel-total-signups">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center">
              <UserPlus className="w-5 h-5 text-white" />
            </div>
          </div>
          <p className="text-3xl font-bold text-[#1D1D1F] tracking-tight" style={heading}>{funnel.total_signups}</p>
          <p className="text-[13px] text-[#86868B] mt-1">Accounts Created</p>
        </motion.div>
        <motion.div className="bg-white rounded-xl border border-gray-100 p-5 shadow-sm"
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}
          data-testid="funnel-overall-rate">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-white" />
            </div>
          </div>
          <p className="text-3xl font-bold text-[#1D1D1F] tracking-tight" style={heading}>{funnel.overall_conversion}%</p>
          <p className="text-[13px] text-[#86868B] mt-1">Overall Conversion</p>
        </motion.div>
      </div>

      {/* Visual Funnel */}
      <motion.div className="bg-white rounded-xl border border-gray-100 p-6 shadow-sm"
        initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
        data-testid="funnel-chart">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-[15px] font-semibold text-[#1D1D1F]" style={heading}>Conversion Funnel</h2>
            <p className="text-[11px] text-[#86868B]">From landing page visit to first login</p>
          </div>
          <GitMerge className="w-5 h-5 text-[#86868B]" />
        </div>

        <div className="space-y-0">
          {stages.map((stage, i) => {
            const Icon = FUNNEL_ICONS[i] || Eye;
            const color = FUNNEL_COLORS[i];
            const widthPct = maxCount > 0 ? Math.max((stage.count / maxCount) * 100, 8) : 8;
            const prevCount = i > 0 ? stages[i - 1].count : stage.count;

            return (
              <div key={stage.id}>
                <motion.div className="relative"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.25 + i * 0.1, duration: 0.4 }}>
                  <div className="flex items-center gap-4">
                    {/* Icon */}
                    <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${color.bg} flex items-center justify-center shrink-0 shadow-sm`}>
                      <Icon className="w-5 h-5 text-white" />
                    </div>

                    {/* Bar + Info */}
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="text-[13px] font-semibold text-[#1D1D1F]">{stage.label}</span>
                        <div className="flex items-center gap-3">
                          <span className="text-[18px] font-bold text-[#1D1D1F]" style={heading}>{stage.count}</span>
                          {i > 0 && (
                            <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${
                              stage.conversion_rate >= 50 ? 'bg-success/10 text-success' :
                              stage.conversion_rate >= 25 ? 'bg-warning/10 text-warning' :
                              'bg-error/10 text-error'
                            }`}>
                              {stage.conversion_rate}%
                            </span>
                          )}
                        </div>
                      </div>
                      {/* Funnel Bar */}
                      <div className="relative h-10 bg-gray-50 rounded-xl overflow-hidden">
                        <motion.div
                          className="absolute inset-y-0 left-0 rounded-xl"
                          style={{
                            background: `linear-gradient(90deg, ${color.bar}22, ${color.bar}40)`,
                            borderLeft: `4px solid ${color.bar}`,
                          }}
                          initial={{ width: 0 }}
                          animate={{ width: `${widthPct}%` }}
                          transition={{ duration: 0.7, delay: 0.3 + i * 0.12, ease: [0.25, 0.1, 0.25, 1] }}
                        />
                        <div className="absolute inset-y-0 left-4 flex items-center">
                          <span className="text-[10px] font-medium text-[#86868B]">
                            {i === 0 ? '100% of visitors' : `${stage.conversion_rate}% of ${stages[i-1].label.toLowerCase()}`}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>

                {/* Drop-off Indicator */}
                {i < stages.length - 1 && (
                  <motion.div className="flex items-center gap-4 py-2"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.4 + i * 0.12 }}>
                    <div className="w-10 flex justify-center">
                      <ArrowDown className="w-4 h-4 text-gray-300" />
                    </div>
                    <div className="flex-1 flex items-center gap-2">
                      <div className="flex-1 h-px bg-gray-100" />
                      {stages[i + 1].dropoff > 0 && (
                        <span className="text-[10px] font-medium text-red-400 bg-error/10 px-2 py-0.5 rounded-full whitespace-nowrap">
                          {stages[i + 1].dropoff} dropped ({stages[i + 1].dropoff_rate}%)
                        </span>
                      )}
                      <div className="flex-1 h-px bg-gray-100" />
                    </div>
                  </motion.div>
                )}
              </div>
            );
          })}
        </div>
      </motion.div>

      {/* Stage-by-Stage Breakdown */}
      <motion.div className="bg-white rounded-xl border border-gray-100 p-5 shadow-sm"
        initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }}
        data-testid="funnel-breakdown">
        <h2 className="text-[15px] font-semibold text-[#1D1D1F] mb-4" style={heading}>Stage Breakdown</h2>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="text-[10px] font-bold text-[#86868B] uppercase tracking-wider border-b border-gray-100">
                <th className="py-2.5 px-3 text-left">Stage</th>
                <th className="py-2.5 px-3 text-center">Count</th>
                <th className="py-2.5 px-3 text-center">Conversion Rate</th>
                <th className="py-2.5 px-3 text-center">Drop-off</th>
                <th className="py-2.5 px-3 text-left">Visual</th>
              </tr>
            </thead>
            <tbody>
              {stages.map((stage, i) => {
                const Icon = FUNNEL_ICONS[i] || Eye;
                const color = FUNNEL_COLORS[i];
                const widthPct = maxCount > 0 ? Math.max((stage.count / maxCount) * 100, 5) : 5;
                return (
                  <tr key={stage.id} className="border-b border-gray-50 hover:bg-gray-50/50"
                    style={{ animation: `fadeIn 0.3s ${0.55 + i * 0.06}s both` }}>
                    <td className="py-3 px-3">
                      <div className="flex items-center gap-2.5">
                        <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${color.bg} flex items-center justify-center`}>
                          <Icon className="w-4 h-4 text-white" />
                        </div>
                        <span className="text-[12px] font-medium text-[#1D1D1F]">{stage.label}</span>
                      </div>
                    </td>
                    <td className="py-3 px-3 text-center">
                      <span className="text-[14px] font-bold text-[#1D1D1F]">{stage.count}</span>
                    </td>
                    <td className="py-3 px-3 text-center">
                      <span className={`text-[11px] font-bold px-2.5 py-1 rounded-full ${
                        i === 0 ? 'bg-info/10 text-info' :
                        stage.conversion_rate >= 50 ? 'bg-success/10 text-success' :
                        stage.conversion_rate >= 25 ? 'bg-warning/10 text-warning' :
                        'bg-error/10 text-error'
                      }`}>
                        {i === 0 ? 'Entry' : `${stage.conversion_rate}%`}
                      </span>
                    </td>
                    <td className="py-3 px-3 text-center">
                      {stage.dropoff > 0 ? (
                        <span className="text-[11px] font-medium text-error">-{stage.dropoff} ({stage.dropoff_rate}%)</span>
                      ) : (
                        <span className="text-[11px] text-[#86868B]">—</span>
                      )}
                    </td>
                    <td className="py-3 px-3">
                      <div className="w-32 h-2.5 bg-gray-100 rounded-full overflow-hidden">
                        <motion.div className="h-full rounded-full"
                          style={{ background: color.bar }}
                          initial={{ width: 0 }}
                          animate={{ width: `${widthPct}%` }}
                          transition={{ duration: 0.5, delay: 0.6 + i * 0.06 }} />
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </motion.div>

      {/* Insights */}
      <motion.div className="bg-white rounded-xl border border-gray-100 p-5 shadow-sm"
        initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6 }}
        data-testid="funnel-insights">
        <h2 className="text-[15px] font-semibold text-[#1D1D1F] mb-4" style={heading}>Funnel Insights</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {(() => {
            const biggestDrop = stages.reduce((max, s, i) => {
              if (i === 0) return max;
              return (s.dropoff_rate > (max?.dropoff_rate || 0)) ? { ...s, index: i } : max;
            }, null);

            const bestConversion = stages.reduce((best, s, i) => {
              if (i === 0) return best;
              return (s.conversion_rate > (best?.conversion_rate || 0)) ? { ...s, index: i } : best;
            }, null);

            return (
              <>
                <div className="bg-error/10 rounded-xl p-4 border border-error/30">
                  <div className="flex items-center gap-2 mb-2">
                    <ArrowDown className="w-4 h-4 text-error" />
                    <span className="text-[11px] font-bold text-error">Biggest Drop-off</span>
                  </div>
                  {biggestDrop ? (
                    <>
                      <p className="text-[15px] font-bold text-[#1D1D1F]" style={heading}>
                        {stages[biggestDrop.index - 1]?.label} → {biggestDrop.label}
                      </p>
                      <p className="text-[10px] text-[#86868B] mt-0.5">
                        {biggestDrop.dropoff} visitors lost ({biggestDrop.dropoff_rate}% drop-off)
                      </p>
                    </>
                  ) : (
                    <p className="text-[13px] text-[#86868B]">Not enough data</p>
                  )}
                </div>
                <div className="bg-success/10 rounded-xl p-4 border border-success/30">
                  <div className="flex items-center gap-2 mb-2">
                    <TrendingUp className="w-4 h-4 text-success" />
                    <span className="text-[11px] font-bold text-success">Best Conversion</span>
                  </div>
                  {bestConversion ? (
                    <>
                      <p className="text-[15px] font-bold text-[#1D1D1F]" style={heading}>
                        {stages[bestConversion.index - 1]?.label} → {bestConversion.label}
                      </p>
                      <p className="text-[10px] text-[#86868B] mt-0.5">
                        {bestConversion.conversion_rate}% conversion rate
                      </p>
                    </>
                  ) : (
                    <p className="text-[13px] text-[#86868B]">Not enough data</p>
                  )}
                </div>
              </>
            );
          })()}
        </div>
      </motion.div>
    </div>
  );
}

/* ═══════════════════════════════════════════
   A/B TESTING VIEW
   ═══════════════════════════════════════════ */

const ALL_STEPS = [
  { id: 'dashboard', label: 'Dashboard' }, { id: 'pos', label: 'POS' },
  { id: 'hr', label: 'HR & Payroll' }, { id: 'accounting', label: 'Accounting' },
  { id: 'analytics', label: 'Analytics' }, { id: 'notifications', label: 'Notifications' },
  { id: 'documents', label: 'Documents' }, { id: 'branches', label: 'Branches' },
  { id: 'alerts', label: 'Alerts' }, { id: 'expenses', label: 'Expenses' },
  { id: 'pnl', label: 'Profit & Loss' },
];

function CreateVariantForm({ onCreated, onCancel }) {
  const [name, setName] = useState('');
  const [steps, setSteps] = useState(ALL_STEPS.map(s => s.id));
  const [isControl, setIsControl] = useState(false);
  const [weight, setWeight] = useState(50);
  const [saving, setSaving] = useState(false);

  const moveStep = (from, to) => {
    const arr = [...steps];
    const [item] = arr.splice(from, 1);
    arr.splice(to, 0, item);
    setSteps(arr);
  };

  const toggleStep = (id) => {
    setSteps(prev => prev.includes(id) ? prev.filter(s => s !== id) : [...prev, id]);
  };

  const handleSubmit = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      await api.post('/demo-analytics/ab/variants', {
        name: name.trim(),
        step_order: steps,
        descriptions: {},
        is_control: isControl,
        traffic_weight: weight,
        active: true,
      });
      onCreated();
    } catch {
      // ignore
    }
    setSaving(false);
  };

  return (
    <motion.div className="bg-white rounded-xl border border-gray-100 p-5 shadow-sm"
      initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
      data-testid="create-variant-form">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-[15px] font-semibold text-[#1D1D1F]" style={heading}>Create New Variant</h3>
        <button onClick={onCancel} className="w-7 h-7 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center">
          <X className="w-4 h-4 text-[#86868B]" />
        </button>
      </div>
      <div className="space-y-4">
        <div>
          <label className="text-[11px] font-medium text-[#86868B] uppercase tracking-wider">Variant Name</label>
          <input value={name} onChange={e => setName(e.target.value)}
            className="mt-1 w-full h-9 px-3 rounded-lg border border-gray-200 text-[13px] text-[#1D1D1F] focus:border-orange-300 focus:ring-1 focus:ring-orange-200 outline-none"
            placeholder="e.g., POS First, Short Tour"
            data-testid="variant-name-input" />
        </div>
        <div className="flex items-center gap-6">
          <div className="flex-1">
            <label className="text-[11px] font-medium text-[#86868B] uppercase tracking-wider">Traffic Weight (%)</label>
            <input type="number" value={weight} onChange={e => setWeight(Number(e.target.value))}
              min={1} max={100}
              className="mt-1 w-full h-9 px-3 rounded-lg border border-gray-200 text-[13px] text-[#1D1D1F] focus:border-orange-300 focus:ring-1 focus:ring-orange-200 outline-none"
              data-testid="variant-weight-input" />
          </div>
          <div className="flex items-center gap-2 pt-5">
            <button onClick={() => setIsControl(!isControl)}
              className={`w-9 h-5 rounded-full transition-colors ${isControl ? 'bg-orange-500' : 'bg-gray-200'}`}
              data-testid="variant-control-toggle">
              <div className={`w-4 h-4 rounded-full bg-white shadow transition-transform ${isControl ? 'translate-x-4.5 ml-[18px]' : 'translate-x-0.5 ml-0.5'}`} />
            </button>
            <span className="text-[11px] text-[#86868B]">Control variant</span>
          </div>
        </div>
        <div>
          <label className="text-[11px] font-medium text-[#86868B] uppercase tracking-wider mb-2 block">Step Order (drag to reorder, click to toggle)</label>
          <div className="space-y-1">
            {steps.map((sid, i) => {
              const label = ALL_STEPS.find(s => s.id === sid)?.label || sid;
              const Icon = STEP_ICONS[sid] || BarChart3;
              return (
                <div key={sid} className="flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-2">
                  <GripVertical className="w-3.5 h-3.5 text-gray-300" />
                  <Icon className="w-3.5 h-3.5 text-[#86868B]" />
                  <span className="text-[12px] font-medium text-[#1D1D1F] flex-1">{label}</span>
                  <div className="flex items-center gap-1">
                    {i > 0 && (
                      <button onClick={() => moveStep(i, i - 1)} className="w-5 h-5 rounded bg-white border border-gray-200 flex items-center justify-center text-[10px] text-[#86868B] hover:bg-gray-100">
                        ↑
                      </button>
                    )}
                    {i < steps.length - 1 && (
                      <button onClick={() => moveStep(i, i + 1)} className="w-5 h-5 rounded bg-white border border-gray-200 flex items-center justify-center text-[10px] text-[#86868B] hover:bg-gray-100">
                        ↓
                      </button>
                    )}
                    <button onClick={() => toggleStep(sid)} className="w-5 h-5 rounded bg-error/10 border border-error/30 flex items-center justify-center hover:bg-error/20">
                      <X className="w-3 h-3 text-error" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
          {/* Removed steps */}
          {ALL_STEPS.filter(s => !steps.includes(s.id)).length > 0 && (
            <div className="mt-2">
              <span className="text-[10px] text-[#86868B]">Removed steps (click to re-add):</span>
              <div className="flex flex-wrap gap-1 mt-1">
                {ALL_STEPS.filter(s => !steps.includes(s.id)).map(s => (
                  <button key={s.id} onClick={() => toggleStep(s.id)}
                    className="text-[10px] px-2 py-1 bg-gray-100 rounded-md text-[#86868B] hover:bg-gray-200">
                    + {s.label}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
        <button onClick={handleSubmit} disabled={!name.trim() || saving}
          className="w-full h-10 bg-[#1D1D1F] hover:bg-black text-white rounded-xl text-[13px] font-semibold disabled:opacity-50 transition-colors"
          data-testid="save-variant-btn">
          {saving ? 'Creating...' : 'Create Variant'}
        </button>
      </div>
    </motion.div>
  );
}

function ABTestingView({ abResults, onRefresh }) {
  const [showCreate, setShowCreate] = useState(false);
  const results = abResults?.results || [];
  const winner = abResults?.winner;
  const autoOptimize = abResults?.auto_optimize_active;
  const totalSessions = abResults?.total_sessions || 0;
  const maxCompletionRate = Math.max(...results.map(r => r.completion_rate), 1);

  const handleDelete = async (name) => {
    try {
      await api.delete(`/demo-analytics/ab/variants/${encodeURIComponent(name)}`);
      onRefresh();
    } catch { /* ignore */ }
  };

  const handleToggle = async (name, currentActive) => {
    try {
      await api.put(`/demo-analytics/ab/variants/${encodeURIComponent(name)}`, { active: !currentActive });
      onRefresh();
    } catch { /* ignore */ }
  };

  return (
    <div className="space-y-6" data-testid="ab-testing-view">
      {/* Header + Create Button */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2">
            <FlaskConical className="w-5 h-5 text-[#86868B]" />
            <span className="text-[13px] text-[#86868B]">{results.length} variant{results.length !== 1 ? 's' : ''} · {totalSessions} total sessions</span>
          </div>
        </div>
        <button onClick={() => setShowCreate(true)}
          className="flex items-center gap-2 px-4 py-2 bg-[#1D1D1F] hover:bg-black text-white rounded-xl text-[13px] font-medium transition-colors"
          data-testid="create-variant-btn">
          <Plus className="w-4 h-4" /> New Variant
        </button>
      </div>

      {/* Auto-Optimize Status */}
      <motion.div className={`rounded-xl p-4 border ${autoOptimize ? 'bg-success/10 border-success/30' : 'bg-gray-50 border-gray-200'}`}
        initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}
        data-testid="auto-optimize-status">
        <div className="flex items-center gap-3">
          <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${autoOptimize ? 'bg-success' : 'bg-gray-400'}`}>
            <Zap className="w-4 h-4 text-white" />
          </div>
          <div>
            <p className="text-[13px] font-semibold text-[#1D1D1F]">
              Auto-Optimize: {autoOptimize ? 'Active' : 'Waiting for data'}
            </p>
            <p className="text-[11px] text-[#86868B]">
              {autoOptimize
                ? 'Traffic is automatically shifting toward the best-performing variant'
                : 'Needs 2+ active variants and 10+ total sessions to activate. Traffic will start shifting at 20+ sessions.'}
            </p>
          </div>
          {winner && (
            <div className="ml-auto flex items-center gap-1.5 px-3 py-1.5 bg-warning/20 rounded-lg border border-warning/30">
              <Trophy className="w-4 h-4 text-warning" />
              <span className="text-[11px] font-bold text-warning">Leader: {winner}</span>
            </div>
          )}
        </div>
      </motion.div>

      {/* Create Variant Form */}
      {showCreate && (
        <CreateVariantForm
          onCreated={() => { setShowCreate(false); onRefresh(); }}
          onCancel={() => setShowCreate(false)}
        />
      )}

      {/* Variants Comparison */}
      {results.length > 0 ? (
        <motion.div className="bg-white rounded-xl border border-gray-100 p-5 shadow-sm"
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
          data-testid="variants-comparison">
          <h2 className="text-[15px] font-semibold text-[#1D1D1F] mb-4" style={heading}>Variant Performance</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {results.map((r, i) => {
              const isWinner = r.name === winner;
              const barWidth = maxCompletionRate > 0 ? Math.max((r.completion_rate / maxCompletionRate) * 100, 5) : 5;
              return (
                <motion.div key={r.name}
                  className={`rounded-xl border p-4 ${isWinner ? 'border-warning/30 bg-warning/10' : 'border-gray-100 bg-white'}`}
                  initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.15 + i * 0.08 }}
                  data-testid={`variant-card-${r.name}`}>
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      {isWinner && <Trophy className="w-4 h-4 text-warning" />}
                      <span className="text-[14px] font-semibold text-[#1D1D1F]">{r.name}</span>
                      {r.is_control && (
                        <span className="text-[8px] font-bold uppercase tracking-wider bg-info/10 text-info px-1.5 py-0.5 rounded">Control</span>
                      )}
                    </div>
                    <div className="flex items-center gap-1">
                      <button onClick={() => handleToggle(r.name, r.active)}
                        className={`w-8 h-4.5 rounded-full transition-colors ${r.active ? 'bg-success' : 'bg-gray-300'}`}
                        data-testid={`toggle-${r.name}`}>
                        <div className={`w-3.5 h-3.5 rounded-full bg-white shadow-sm transition-transform ${r.active ? 'ml-[17px]' : 'ml-0.5'}`} />
                      </button>
                      <button onClick={() => handleDelete(r.name)}
                        className="w-6 h-6 rounded-full hover:bg-error/10 flex items-center justify-center"
                        data-testid={`delete-${r.name}`}>
                        <X className="w-3 h-3 text-gray-400 hover:text-error" />
                      </button>
                    </div>
                  </div>

                  {/* Metrics */}
                  <div className="grid grid-cols-2 gap-3 mb-3">
                    <div>
                      <p className="text-[22px] font-bold text-[#1D1D1F]" style={heading}>{r.completion_rate}%</p>
                      <p className="text-[9px] text-[#86868B]">Completion Rate</p>
                    </div>
                    <div>
                      <p className="text-[22px] font-bold text-[#1D1D1F]" style={heading}>{r.sessions}</p>
                      <p className="text-[9px] text-[#86868B]">Sessions</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2 mb-3">
                    <div className="bg-gray-50 rounded-lg p-2 text-center">
                      <p className="text-[13px] font-bold text-[#1D1D1F]">{r.completed}</p>
                      <p className="text-[8px] text-[#86868B]">Completed</p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-2 text-center">
                      <p className="text-[13px] font-bold text-[#1D1D1F]">{r.avg_steps}</p>
                      <p className="text-[8px] text-[#86868B]">Avg Steps</p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-2 text-center">
                      <p className="text-[13px] font-bold text-[#1D1D1F]">{r.avg_time}s</p>
                      <p className="text-[8px] text-[#86868B]">Avg Time</p>
                    </div>
                  </div>

                  {/* Completion Bar */}
                  <div className="h-2.5 bg-gray-100 rounded-full overflow-hidden">
                    <motion.div className="h-full rounded-full"
                      style={{ background: isWinner ? 'linear-gradient(90deg, #f59e0b, #f97316)' : 'linear-gradient(90deg, #3b82f6, #6366f1)' }}
                      initial={{ width: 0 }}
                      animate={{ width: `${barWidth}%` }}
                      transition={{ duration: 0.6, delay: 0.3 + i * 0.1 }} />
                  </div>

                  {/* Traffic Weight */}
                  <div className="mt-3 flex items-center justify-between">
                    <span className="text-[10px] text-[#86868B]">Traffic Weight</span>
                    <span className="text-[11px] font-bold text-[#1D1D1F]">{r.traffic_weight}%</span>
                  </div>

                  {/* Step Order Preview */}
                  <div className="mt-2 flex flex-wrap gap-1">
                    {(r.step_order || []).slice(0, 5).map(sid => (
                      <span key={sid} className="text-[8px] bg-gray-100 text-[#86868B] px-1.5 py-0.5 rounded font-medium">
                        {STEP_LABELS[sid] || sid}
                      </span>
                    ))}
                    {(r.step_order || []).length > 5 && (
                      <span className="text-[8px] text-[#86868B]">+{(r.step_order || []).length - 5} more</span>
                    )}
                  </div>

                  {r.auto_optimized && (
                    <div className="mt-2 flex items-center gap-1">
                      <Zap className="w-3 h-3 text-success" />
                      <span className="text-[9px] text-success font-medium">Auto-optimized weight</span>
                    </div>
                  )}
                </motion.div>
              );
            })}
          </div>
        </motion.div>
      ) : (
        <motion.div className="bg-white rounded-xl border border-gray-100 p-10 shadow-sm text-center"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }}
          data-testid="no-variants">
          <FlaskConical className="w-12 h-12 text-gray-200 mx-auto mb-3" />
          <p className="text-[15px] font-semibold text-[#1D1D1F]" style={heading}>No Variants Yet</p>
          <p className="text-[13px] text-[#86868B] mt-1 mb-4">Create your first A/B test variant to start optimizing your demo tour</p>
          <button onClick={() => setShowCreate(true)}
            className="px-5 py-2.5 bg-[#1D1D1F] hover:bg-black text-white rounded-xl text-[13px] font-semibold transition-colors">
            Create First Variant
          </button>
        </motion.div>
      )}

      {/* How It Works */}
      <motion.div className="bg-gray-50 rounded-xl border border-gray-100 p-5"
        initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
        data-testid="ab-how-it-works">
        <h3 className="text-[13px] font-semibold text-[#1D1D1F] mb-3" style={heading}>How A/B Testing Works</h3>
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
          {[
            { step: '1', title: 'Create Variants', desc: 'Define different step orders and descriptions for each variant', icon: Plus },
            { step: '2', title: 'Split Traffic', desc: 'Visitors are randomly assigned to variants based on weights', icon: Users },
            { step: '3', title: 'Measure', desc: 'Track completion rates, avg steps, and time per variant', icon: BarChart3 },
            { step: '4', title: 'Auto-Optimize', desc: 'Traffic automatically shifts to the best performing variant', icon: Zap },
          ].map((item, i) => (
            <div key={item.step} className="text-center">
              <div className="w-9 h-9 rounded-xl bg-white border border-gray-200 flex items-center justify-center mx-auto mb-2">
                <item.icon className="w-4 h-4 text-[#86868B]" />
              </div>
              <p className="text-[11px] font-semibold text-[#1D1D1F]">{item.title}</p>
              <p className="text-[10px] text-[#86868B] mt-0.5">{item.desc}</p>
            </div>
          ))}
        </div>
      </motion.div>
    </div>
  );
}



/* ═══════════════════════════════════════════
   ALERTS VIEW
   ═══════════════════════════════════════════ */

const SEVERITY_CONFIG = {
  critical: { bg: 'bg-error/10', border: 'border-error/30', icon: ShieldAlert, iconColor: 'text-error', badgeBg: 'bg-error', label: 'Critical' },
  warning: { bg: 'bg-warning/10', border: 'border-warning/30', icon: AlertTriangle, iconColor: 'text-warning', badgeBg: 'bg-warning', label: 'Warning' },
  info: { bg: 'bg-info/10', border: 'border-info/30', icon: AlertCircle, iconColor: 'text-info', badgeBg: 'bg-info', label: 'Info' },
};

function AlertsView({ alertsData, onRefresh }) {
  const [showSettings, setShowSettings] = useState(false);
  const [thresholds, setThresholds] = useState(alertsData?.thresholds || {
    completion_rate_low: 30, traffic_drop_pct: 40, variant_outperform_pct: 20, min_sessions_for_alerts: 5,
  });
  const [saving, setSaving] = useState(false);

  const alerts = alertsData?.alerts || [];
  const history = alertsData?.history || [];
  const criticalCount = alerts.filter(a => a.severity === 'critical').length;
  const warningCount = alerts.filter(a => a.severity === 'warning').length;
  const infoCount = alerts.filter(a => a.severity === 'info').length;

  const saveThresholds = async () => {
    setSaving(true);
    try {
      await api.post('/demo-analytics/alerts/settings', thresholds);
      onRefresh();
    } catch { /* ignore */ }
    setSaving(false);
    setShowSettings(false);
  };

  return (
    <div className="space-y-6" data-testid="alerts-view">
      {/* Header with settings */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <BellRing className="w-5 h-5 text-[#86868B]" />
          <span className="text-[13px] text-[#86868B]">
            {alerts.length} active alert{alerts.length !== 1 ? 's' : ''}
          </span>
        </div>
        <button onClick={() => setShowSettings(!showSettings)}
          className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-xl text-[13px] font-medium text-[#1D1D1F] transition-colors"
          data-testid="alert-settings-btn">
          <Settings className="w-4 h-4" /> Thresholds
        </button>
      </div>

      {/* Severity Summary */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
        <motion.div className="bg-error/10 rounded-xl border border-error/30 p-4"
          initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}
          data-testid="alert-summary-critical">
          <div className="flex items-center gap-2 mb-2">
            <ShieldAlert className="w-4 h-4 text-error" />
            <span className="text-[11px] font-bold text-error uppercase tracking-wider">Critical</span>
          </div>
          <p className="text-3xl font-bold text-[#1D1D1F]" style={heading}>{criticalCount}</p>
        </motion.div>
        <motion.div className="bg-warning/10 rounded-xl border border-warning/30 p-4"
          initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
          data-testid="alert-summary-warning">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="w-4 h-4 text-warning" />
            <span className="text-[11px] font-bold text-warning uppercase tracking-wider">Warning</span>
          </div>
          <p className="text-3xl font-bold text-[#1D1D1F]" style={heading}>{warningCount}</p>
        </motion.div>
        <motion.div className="bg-info/10 rounded-xl border border-info/30 p-4"
          initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}
          data-testid="alert-summary-info">
          <div className="flex items-center gap-2 mb-2">
            <AlertCircle className="w-4 h-4 text-info" />
            <span className="text-[11px] font-bold text-info uppercase tracking-wider">Info</span>
          </div>
          <p className="text-3xl font-bold text-[#1D1D1F]" style={heading}>{infoCount}</p>
        </motion.div>
      </div>

      {/* Settings Panel */}
      {showSettings && (
        <motion.div className="bg-white rounded-xl border border-gray-100 p-5 shadow-sm"
          initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }}
          data-testid="alert-settings-panel">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-[15px] font-semibold text-[#1D1D1F]" style={heading}>Alert Thresholds</h3>
            <button onClick={() => setShowSettings(false)} className="w-7 h-7 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center">
              <X className="w-4 h-4 text-[#86868B]" />
            </button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="text-[11px] font-medium text-[#86868B] uppercase tracking-wider">Low Completion Rate (%)</label>
              <input type="number" value={thresholds.completion_rate_low}
                onChange={e => setThresholds(p => ({ ...p, completion_rate_low: Number(e.target.value) }))}
                className="mt-1 w-full h-9 px-3 rounded-lg border border-gray-200 text-[13px] text-[#1D1D1F] focus:border-orange-300 focus:ring-1 focus:ring-orange-200 outline-none"
                data-testid="threshold-completion" />
              <p className="text-[9px] text-[#86868B] mt-1">Alert when rate drops below this</p>
            </div>
            <div>
              <label className="text-[11px] font-medium text-[#86868B] uppercase tracking-wider">Traffic Drop (%)</label>
              <input type="number" value={thresholds.traffic_drop_pct}
                onChange={e => setThresholds(p => ({ ...p, traffic_drop_pct: Number(e.target.value) }))}
                className="mt-1 w-full h-9 px-3 rounded-lg border border-gray-200 text-[13px] text-[#1D1D1F] focus:border-orange-300 focus:ring-1 focus:ring-orange-200 outline-none"
                data-testid="threshold-traffic" />
              <p className="text-[9px] text-[#86868B] mt-1">Week-over-week drop threshold</p>
            </div>
            <div>
              <label className="text-[11px] font-medium text-[#86868B] uppercase tracking-wider">Variant Outperform (%)</label>
              <input type="number" value={thresholds.variant_outperform_pct}
                onChange={e => setThresholds(p => ({ ...p, variant_outperform_pct: Number(e.target.value) }))}
                className="mt-1 w-full h-9 px-3 rounded-lg border border-gray-200 text-[13px] text-[#1D1D1F] focus:border-orange-300 focus:ring-1 focus:ring-orange-200 outline-none"
                data-testid="threshold-variant" />
              <p className="text-[9px] text-[#86868B] mt-1">When variant beats control by X%</p>
            </div>
            <div>
              <label className="text-[11px] font-medium text-[#86868B] uppercase tracking-wider">Min Sessions</label>
              <input type="number" value={thresholds.min_sessions_for_alerts}
                onChange={e => setThresholds(p => ({ ...p, min_sessions_for_alerts: Number(e.target.value) }))}
                className="mt-1 w-full h-9 px-3 rounded-lg border border-gray-200 text-[13px] text-[#1D1D1F] focus:border-orange-300 focus:ring-1 focus:ring-orange-200 outline-none"
                data-testid="threshold-min-sessions" />
              <p className="text-[9px] text-[#86868B] mt-1">Minimum data before alerting</p>
            </div>
          </div>
          <button onClick={saveThresholds} disabled={saving}
            className="mt-4 flex items-center gap-2 px-5 py-2.5 bg-[#1D1D1F] hover:bg-black text-white rounded-xl text-[13px] font-semibold disabled:opacity-50 transition-colors"
            data-testid="save-thresholds-btn">
            <Save className="w-4 h-4" /> {saving ? 'Saving...' : 'Save Thresholds'}
          </button>
        </motion.div>
      )}

      {/* Active Alerts */}
      {alerts.length > 0 ? (
        <div className="space-y-3">
          {alerts.map((alert, i) => {
            const config = SEVERITY_CONFIG[alert.severity] || SEVERITY_CONFIG.info;
            const Icon = config.icon;
            return (
              <motion.div key={alert.id}
                className={`rounded-xl border p-5 ${config.bg} ${config.border}`}
                initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.1 + i * 0.08 }}
                data-testid={`alert-${alert.id}`}>
                <div className="flex items-start gap-4">
                  <div className={`w-10 h-10 rounded-xl ${config.badgeBg} flex items-center justify-center shrink-0`}>
                    <Icon className="w-5 h-5 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="text-[14px] font-semibold text-[#1D1D1F]">{alert.title}</h3>
                      <span className={`text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full text-white ${config.badgeBg}`}>
                        {config.label}
                      </span>
                    </div>
                    <p className="text-[13px] text-[#1D1D1F]/70 leading-relaxed">{alert.message}</p>
                    {(alert.value !== undefined && alert.threshold !== undefined) && (
                      <div className="flex items-center gap-4 mt-3">
                        <div className="flex items-center gap-1.5">
                          <span className="text-[10px] text-[#86868B]">Current:</span>
                          <span className="text-[12px] font-bold text-[#1D1D1F]">{alert.value}%</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <span className="text-[10px] text-[#86868B]">Threshold:</span>
                          <span className="text-[12px] font-bold text-[#1D1D1F]">{alert.threshold}%</span>
                        </div>
                      </div>
                    )}
                  </div>
                  <span className="text-[10px] text-[#86868B] whitespace-nowrap shrink-0">
                    {new Date(alert.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </span>
                </div>
              </motion.div>
            );
          })}
        </div>
      ) : (
        <motion.div className="bg-white rounded-xl border border-gray-100 p-10 shadow-sm text-center"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }}
          data-testid="no-alerts">
          <CheckCircle className="w-12 h-12 text-emerald-300 mx-auto mb-3" />
          <p className="text-[15px] font-semibold text-[#1D1D1F]" style={heading}>All Clear</p>
          <p className="text-[13px] text-[#86868B] mt-1">
            {alertsData?.total_sessions < (alertsData?.thresholds?.min_sessions_for_alerts || 5)
              ? `Need at least ${alertsData?.thresholds?.min_sessions_for_alerts || 5} sessions to generate alerts (currently ${alertsData?.total_sessions || 0})`
              : 'No performance issues detected. Your demo tour is running smoothly!'}
          </p>
        </motion.div>
      )}

      {/* Alert History */}
      <motion.div className="bg-white rounded-xl border border-gray-100 p-5 shadow-sm"
        initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
        data-testid="alert-history">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-[15px] font-semibold text-[#1D1D1F]" style={heading}>Alert History</h2>
            <p className="text-[11px] text-[#86868B]">Previously triggered alerts</p>
          </div>
          <Clock className="w-5 h-5 text-[#86868B]" />
        </div>
        {history.length > 0 ? (
          <div className="space-y-2">
            {history.map((h, i) => {
              const config = SEVERITY_CONFIG[h.severity] || SEVERITY_CONFIG.info;
              const Icon = config.icon;
              return (
                <div key={h.id + i} className="flex items-center gap-3 py-2.5 px-3 bg-gray-50 rounded-lg"
                  style={{ animation: `fadeIn 0.3s ${0.35 + i * 0.04}s both` }}>
                  <div className={`w-7 h-7 rounded-lg ${config.badgeBg} flex items-center justify-center shrink-0`}>
                    <Icon className="w-3.5 h-3.5 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[12px] font-medium text-[#1D1D1F] truncate">{h.title}</p>
                    <p className="text-[10px] text-[#86868B] truncate">{h.message}</p>
                  </div>
                  <span className="text-[10px] text-[#86868B] whitespace-nowrap">
                    {h.last_triggered ? new Date(h.last_triggered).toLocaleDateString() : ''}
                  </span>
                </div>
              );
            })}
          </div>
        ) : (
          <p className="text-[13px] text-[#86868B] text-center py-6">No alert history yet</p>
        )}
      </motion.div>

      {/* How Alerts Work */}
      <motion.div className="bg-gray-50 rounded-xl border border-gray-100 p-5"
        initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}
        data-testid="alerts-how-it-works">
        <h3 className="text-[13px] font-semibold text-[#1D1D1F] mb-3" style={heading}>What Triggers Alerts</h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {[
            { icon: TrendingDown, title: 'Low Completion', desc: 'Fires when tour completion rate drops below your threshold', severity: 'critical' },
            { icon: AlertTriangle, title: 'Traffic Drop', desc: 'Detects significant drops in weekly demo traffic compared to monthly average', severity: 'warning' },
            { icon: Trophy, title: 'Variant Winner', desc: 'Notifies when an A/B variant outperforms the control by your configured margin', severity: 'info' },
          ].map((item) => (
            <div key={item.title} className="text-center">
              <div className={`w-9 h-9 rounded-xl ${SEVERITY_CONFIG[item.severity].badgeBg} flex items-center justify-center mx-auto mb-2`}>
                <item.icon className="w-4 h-4 text-white" />
              </div>
              <p className="text-[11px] font-semibold text-[#1D1D1F]">{item.title}</p>
              <p className="text-[10px] text-[#86868B] mt-0.5">{item.desc}</p>
            </div>
          ))}
        </div>
      </motion.div>
    </div>
  );
}
